import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import Pagination from "@/components/Pagination";
import {
  Plus, Buildings, Briefcase, User, Calendar, CurrencyGbp, Receipt, EnvelopeSimple,
  Phone, NoteBlank, Trash, X, Eye, CaretRight, MagnifyingGlass, PencilSimple,
  FileText, ShieldCheck, PaperPlaneTilt, Download, WarningCircle
} from "@phosphor-icons/react";

const STAGE_META = {
  new_vacancy: { label: "New Vacancy", color: "#6B7280" }, vacancy_assigned: { label: "Assigned", color: "#3B82F6" },
  candidate_search: { label: "Search", color: "#8B5CF6" }, candidate_shortlisted: { label: "Shortlisted", color: "#A855F7" },
  cv_submitted: { label: "CV Sent", color: "#EC4899" }, client_reviewing: { label: "Reviewing", color: "#F59E0B" },
  interview_requested: { label: "Int. Req.", color: "#F97316" }, interview_scheduled: { label: "Int. Sched.", color: "#EF4444" },
  interview_confirmed: { label: "Int. Conf.", color: "#14B8A6" }, first_interview: { label: "1st Int.", color: "#06B6D4" },
  second_interview: { label: "2nd Int.", color: "#0EA5E9" }, offer_made: { label: "Offer", color: "#10B981" },
  offer_accepted: { label: "Accepted", color: "#22C55E" }, placement_confirmed: { label: "Placed", color: "#16A34A" },
  invoice_generated: { label: "Inv. Gen.", color: "#C5A059" }, invoice_sent: { label: "Inv. Sent", color: "#B8933F" },
  invoice_paid: { label: "Inv. Paid", color: "#1E3A8A" }, placement_complete: { label: "Complete", color: "#0F172A" },
};

const INT_STATUS_COLORS = {
  scheduled: "bg-blue-100 text-blue-700", confirmed: "bg-teal-100 text-teal-700",
  completed: "bg-green-100 text-green-700", cancelled: "bg-gray-100 text-gray-500", no_show: "bg-red-100 text-red-600",
};

export default function VacanciesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [vacancies, setVacancies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [workspace, setWorkspace] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [teams, setTeams] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [allCandidates, setAllCandidates] = useState([]);
  const [form, setForm] = useState({ title: "", company_name: "", salary: "", fee_percentage: "15", location: "", hours: "", working_pattern: "", description: "", job_type: "permanent", sector: "", position_finder_id: "", candidate_finder_id: "", team_id: "", contact_person: "", contact_email: "", contact_phone: "" });
  const [noteText, setNoteText] = useState("");
  const [showAddCandidate, setShowAddCandidate] = useState(false);
  const [showScheduleInterview, setShowScheduleInterview] = useState(false);
  const [editingVacancy, setEditingVacancy] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [interviewForm, setInterviewForm] = useState({ candidate_id: "", candidate_name: "", interview_type: "first", scheduled_at: "", duration_minutes: 60, meeting_platform: "", meeting_link: "", interviewer_name: "", notes: "" });
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [contractForm, setContractForm] = useState({ fee_percentage: "15", guarantee_days: "14", contact_person: "", contact_email: "", payment_terms: "Payment due within 14 days of invoice date.", special_terms: "" });
  const [showCreateContract, setShowCreateContract] = useState(false);

  useEffect(() => { loadVacancies(); loadSupportData(); }, []);

  const loadVacancies = async () => {
    try { const { data } = await api.get(`/vacancies?limit=25&page=${page}&search=${search}`); setVacancies(data.items || []); setTotal(data.total || 0); }
    catch { toast.error("Failed to load vacancies"); }
    finally { setLoading(false); }
  };

  const loadSupportData = async () => {
    try {
      const [t, u, c] = await Promise.all([api.get("/teams").catch(() => ({ data: [] })), api.get("/users").catch(() => ({ data: [] })), api.get("/candidates?limit=200").catch(() => ({ data: { items: [] } }))]);
      setTeams(t.data || []); setAllUsers(u.data || []); setAllCandidates(c.data?.items || []);
    } catch {}
  };

  const openWorkspace = async (vacancyId) => {
    try {
      const { data } = await api.get(`/vacancies/${vacancyId}/workspace`);
      setWorkspace(data);
      setActiveTab("overview");
    } catch { toast.error("Failed to load workspace"); }
  };

  const handleCreate = async () => {
    if (!form.title) { toast.error("Title is required"); return; }
    try {
      const pf = allUsers.find(u => u.id === form.position_finder_id);
      const cf = allUsers.find(u => u.id === form.candidate_finder_id);
      await api.post("/vacancies", { ...form, fee_percentage: parseFloat(form.fee_percentage || "15"), position_finder_name: pf?.name || "", candidate_finder_name: cf?.name || "" });
      toast.success("Vacancy created");
      setShowCreate(false);
      setForm({ title: "", company_name: "", salary: "", fee_percentage: "15", location: "", hours: "", working_pattern: "", description: "", job_type: "permanent", sector: "", position_finder_id: "", candidate_finder_id: "", team_id: "", contact_person: "", contact_email: "", contact_phone: "" });
      loadVacancies();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleAddNote = async () => {
    if (!noteText.trim() || !workspace) return;
    try {
      await api.post(`/vacancies/${workspace.vacancy.id}/notes`, { content: noteText });
      setNoteText("");
      openWorkspace(workspace.vacancy.id);
    } catch { toast.error("Failed to add note"); }
  };

  const handleAddCandidate = async (candidateId) => {
    if (!workspace) return;
    try {
      await api.post("/placements", { vacancy_id: workspace.vacancy.id, candidate_id: candidateId, stage: "candidate_shortlisted" });
      toast.success("Candidate added to pipeline");
      setShowAddCandidate(false);
      openWorkspace(workspace.vacancy.id);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleScheduleInterview = async () => {
    if (!interviewForm.candidate_id || !interviewForm.scheduled_at) { toast.error("Candidate and date/time required"); return; }
    try {
      const placement = workspace.placements.find(p => p.candidate_id === interviewForm.candidate_id);
      await api.post("/interviews", {
        ...interviewForm, vacancy_id: workspace.vacancy.id, placement_id: placement?.id || "",
        company_id: workspace.vacancy.company_id, company_name: workspace.vacancy.company_name,
        duration_minutes: parseInt(interviewForm.duration_minutes),
      });
      toast.success("Interview scheduled");
      setShowScheduleInterview(false);
      setInterviewForm({ candidate_id: "", candidate_name: "", interview_type: "first", scheduled_at: "", duration_minutes: 60, meeting_platform: "", meeting_link: "", interviewer_name: "", notes: "" });
      openWorkspace(workspace.vacancy.id);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleInterviewAction = async (interviewId, action) => {
    try {
      if (action === "confirm") await api.post(`/interviews/${interviewId}/confirm`);
      else if (action === "complete_place") await api.post(`/interviews/${interviewId}/complete-and-place`);
      else await api.put(`/interviews/${interviewId}`, { status: action });
      toast.success("Interview updated");
      openWorkspace(workspace.vacancy.id);
    } catch { toast.error("Failed"); }
  };

  const handleCreateContract = async () => {
    if (!workspace) return;
    try {
      await api.post("/contracts", {
        vacancy_id: workspace.vacancy.id,
        fee_percentage: parseFloat(contractForm.fee_percentage || "15"),
        guarantee_days: parseInt(contractForm.guarantee_days || "14"),
        contact_person: contractForm.contact_person || workspace.vacancy.contact_person || "",
        contact_email: contractForm.contact_email || workspace.vacancy.contact_email || "",
        payment_terms: contractForm.payment_terms,
        special_terms: contractForm.special_terms,
      });
      toast.success("Contract created");
      setShowCreateContract(false);
      openWorkspace(workspace.vacancy.id);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create contract"); }
  };

  const handleSendContract = async (contractId) => {
    try {
      await api.post(`/contracts/${contractId}/send`);
      toast.success("Contract sent to client");
      openWorkspace(workspace.vacancy.id);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to send"); }
  };

  const handleContractOverride = async (contractId) => {
    if (!window.confirm("Allow vacancy to be worked without a signed contract?")) return;
    try {
      await api.put(`/contracts/${contractId}/override`);
      toast.success("Admin override applied");
      openWorkspace(workspace.vacancy.id);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleDownloadContractPdf = async (contractId) => {
    try {
      const response = await api.get(`/contracts/${contractId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `contract-${contractId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download PDF"); }
  };

  const handleArchiveVacancy = async (vacancyId, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm("Archive this vacancy? It will be hidden from the active list.")) return;
    try {
      await api.delete(`/vacancies/${vacancyId}`);
      toast.success("Vacancy archived");
      loadVacancies();
      if (workspace?.vacancy?.id === vacancyId) setWorkspace(null);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to archive"); }
  };

  const handleDeleteInterview = async (interviewId) => {
    if (!window.confirm("Delete this interview? The candidate and vacancy will NOT be affected.")) return;
    try {
      await api.delete(`/interviews/${interviewId}`);
      toast.success("Interview deleted");
      if (workspace) openWorkspace(workspace.vacancy.id);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to delete"); }
  };

  const isContractRequired = workspace && workspace.vacancy &&
    !["signed", "admin_override"].includes(workspace.vacancy.contract_status || "none") &&
    user?.role !== "admin";

  const TABS = [
    { key: "overview", label: "Overview", icon: Eye },
    { key: "contract", label: "Contract", icon: FileText },
    { key: "pipeline", label: "Pipeline", icon: Briefcase },
    { key: "interviews", label: "Interviews", icon: Calendar },
    { key: "notes", label: "Notes", icon: NoteBlank },
    { key: "emails", label: "Emails", icon: EnvelopeSimple },
    { key: "calls", label: "Calls", icon: Phone },
    { key: "commission", label: "Commission", icon: CurrencyGbp },
  ];

  // === VACANCY LIST VIEW ===
  if (!workspace) {
    return (
      <div data-testid="vacancies-page" className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-[#0F172A]">Vacancy Workspace</h1>
            <p className="text-sm text-[#6B7280] mt-1">Each vacancy is a project — all activity in one place</p>
          </div>
          <Button onClick={() => setShowCreate(true)} className="bg-[#0F172A] hover:bg-[#1E293B] gap-2" data-testid="create-vacancy-btn"><Plus size={16} /> New Vacancy</Button>
        </div>
        <div className="flex gap-2">
          <Input placeholder="Search vacancies..." value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === "Enter" && loadVacancies()} className="max-w-xs" data-testid="search-vacancies-input" />
          <Button variant="outline" onClick={loadVacancies}><MagnifyingGlass size={16} /></Button>
        </div>
        {loading ? <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-20 skeleton-shimmer rounded-lg" />)}</div> : vacancies.length === 0 ? (
          <div className="bg-white rounded-lg border p-12 text-center"><Briefcase size={48} className="text-[#D1D5DB] mx-auto mb-3" /><p className="text-[#6B7280]">No vacancies yet</p></div>
        ) : (<>
          <div className="space-y-2">
            {vacancies.map(v => (
              <div key={v.id} role="button" tabIndex={0} onClick={() => openWorkspace(v.id)} onKeyDown={e => e.key === "Enter" && openWorkspace(v.id)} className="w-full bg-white rounded-lg border border-[#E5E7EB] p-4 text-left hover:shadow-md transition-shadow cursor-pointer" data-testid={`vacancy-card-${v.id}`}>
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-[#0F172A] truncate">{v.title}</h3>
                      <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase rounded ${v.status === "open" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>{v.status}</span>
                      {v.public && <span className="px-2 py-0.5 text-[10px] font-semibold uppercase rounded bg-[#D4AF37]/10 text-[#D4AF37]">Live on Website</span>}
                    </div>
                    <p className="text-xs text-[#6B7280] mt-0.5">{v.company_name}{v.location ? ` · ${v.location}` : ""}{v.salary ? ` · £${v.salary}` : ""}</p>
                    {v.created_by_name && <p className="text-[10px] text-[#9CA3AF] mt-0.5">Created by {v.created_by_name}</p>}
                    <div className="flex gap-3 mt-1.5">
                      <span className="text-[10px] text-[#1E3A8A]">{v.placement_count || 0} candidates</span>
                      <span className="text-[10px] text-[#C5A059]">{v.interview_count || 0} interviews</span>
                      {v.fee_percentage && <span className="text-[10px] text-[#10B981]">{v.fee_percentage}% fee</span>}
                      {v.contract_status && v.contract_status !== "none" && (
                        <span className={`text-[10px] font-medium ${v.contract_status === "signed" ? "text-green-600" : v.contract_status === "admin_override" ? "text-amber-600" : "text-blue-600"}`}>
                          {v.contract_status === "signed" ? "Contract Signed" : v.contract_status === "admin_override" ? "Override" : v.contract_status === "sent" ? "Contract Sent" : v.contract_status === "draft" ? "Contract Draft" : v.contract_status}
                        </span>
                      )}
                    </div>
                  </div>
                  <CaretRight size={18} className="text-[#C5A059] flex-shrink-0" />
                  {(user?.role === "admin" || user?.role === "team_leader") && (
                    <button
                      onClick={(e) => handleArchiveVacancy(v.id, e)}
                      className="ml-2 p-1.5 rounded hover:bg-red-50 text-[#9CA3AF] hover:text-[#EF4444] transition-colors flex-shrink-0"
                      title="Archive vacancy"
                      data-testid={`archive-vacancy-${v.id}`}
                    >
                      <Trash size={16} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <Pagination page={page} total={total} limit={25} onPageChange={(p) => { setPage(p); setLoading(true); }} />
        </>)}
        {/* Create Dialog */}
        {showCreate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 mx-4 max-h-[85vh] overflow-y-auto" data-testid="create-vacancy-dialog">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold text-[#0F172A]">Create Vacancy</h2>
                <button onClick={() => setShowCreate(false)}><X size={20} className="text-[#6B7280]" /></button>
              </div>
              <div className="space-y-3">
                <Input placeholder="Job Title *" value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="vacancy-title-input" />
                <Input placeholder="Company Name" value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} data-testid="vacancy-company-input" />
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="Salary" value={form.salary} onChange={e => setForm({...form, salary: e.target.value})} data-testid="vacancy-salary-input" />
                  <Input placeholder="Fee %" type="number" value={form.fee_percentage} onChange={e => setForm({...form, fee_percentage: e.target.value})} />
                </div>
                <Input placeholder="Location" value={form.location} onChange={e => setForm({...form, location: e.target.value})} />
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="Hours (e.g. 37.5 per week)" value={form.hours} onChange={e => setForm({...form, hours: e.target.value})} data-testid="vacancy-hours-input" />
                  <Input placeholder="Working pattern (e.g. Mon-Fri days)" value={form.working_pattern} onChange={e => setForm({...form, working_pattern: e.target.value})} data-testid="vacancy-pattern-input" />
                </div>
                <textarea className="w-full border rounded-lg px-3 py-2 text-sm resize-none" rows={2} placeholder="Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
                <div className="grid grid-cols-2 gap-3">
                  <select className="border rounded-lg px-3 py-2 text-sm" value={form.team_id} onChange={e => setForm({...form, team_id: e.target.value})}>
                    <option value="">Team</option>
                    {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                  <Input placeholder="Sector" value={form.sector} onChange={e => setForm({...form, sector: e.target.value})} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <select className="border rounded-lg px-3 py-2 text-sm" value={form.position_finder_id} onChange={e => setForm({...form, position_finder_id: e.target.value})}>
                    <option value="">Opportunity Finder</option>
                    {allUsers.filter(u => u.is_active).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                  <select className="border rounded-lg px-3 py-2 text-sm" value={form.candidate_finder_id} onChange={e => setForm({...form, candidate_finder_id: e.target.value})}>
                    <option value="">Candidate Finder</option>
                    {allUsers.filter(u => u.is_active).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <Input placeholder="Contact Person" value={form.contact_person} onChange={e => setForm({...form, contact_person: e.target.value})} />
                  <Input placeholder="Email" value={form.contact_email} onChange={e => setForm({...form, contact_email: e.target.value})} />
                  <Input placeholder="Phone" value={form.contact_phone} onChange={e => setForm({...form, contact_phone: e.target.value})} />
                </div>
                <Button onClick={handleCreate} className="w-full bg-[#0F172A] hover:bg-[#1E293B]" data-testid="submit-vacancy-btn">Create Vacancy</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // === WORKSPACE VIEW ===
  const v = workspace.vacancy;
  const salaryNum = parseFloat((v.salary || "0").replace(/[^0-9.]/g, "")) || 0;

  return (
    <div data-testid="vacancy-workspace" className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <button onClick={() => setWorkspace(null)} className="text-xs text-[#1E3A8A] hover:underline mb-1" data-testid="back-to-vacancies">&larr; All Vacancies</button>
          <h1 className="text-xl font-bold text-[#0F172A]">{v.title}</h1>
          <div className="flex flex-wrap gap-2 mt-1">
            {v.company_name && <span className="text-xs text-[#6B7280] flex items-center gap-1"><Buildings size={12} /> {v.company_name}</span>}
            {v.location && <span className="text-xs text-[#6B7280]">{v.location}</span>}
            {v.salary && <span className="text-xs font-medium text-[#C5A059]">{"\u00a3"}{v.salary}</span>}
            <span className="text-xs text-[#10B981] font-medium">{v.fee_percentage}% fee</span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => {
            setEditingVacancy(true);
            setEditForm({ title: v.title, company_name: v.company_name, location: v.location || "", hours: v.hours || "", working_pattern: v.working_pattern || "", salary: v.salary || "", description: v.description || "", job_type: v.job_type || "permanent", sector: v.sector || "", fee_percentage: v.fee_percentage || "15", contact_person: v.contact_person || "", contact_email: v.contact_email || "", contact_phone: v.contact_phone || "" });
          }} className="gap-1" data-testid="edit-vacancy-btn"><PencilSimple size={14} /> Edit</Button>
          <Button variant="outline" size="sm" onClick={() => setShowAddCandidate(true)} className="gap-1" data-testid="add-candidate-pipeline-btn"><User size={14} /> Add Candidate</Button>
          <Button size="sm" onClick={() => setShowScheduleInterview(true)} className="bg-[#0F172A] gap-1" data-testid="schedule-interview-btn"><Calendar size={14} /> Schedule Interview</Button>
          {(user?.role === "admin" || user?.role === "team_leader") ? <Button variant={v.public ? "default" : "outline"} size="sm" className={`gap-1 ${v.public ? "bg-[#D4AF37] hover:bg-[#B8941F] text-white" : "text-[#D4AF37] border-[#D4AF37]"}`} onClick={async () => {
            try {
              const { data } = await api.patch(`/vacancies/${v.id}/public`, { public: !v.public });
              toast.success(data.message);
              loadWorkspace(v.id);
            } catch (e) { toast.error(e?.response?.data?.detail || "Failed to toggle"); }
          }} data-testid="publish-website-toggle">{v.public ? "Live on Website" : "Publish to Website"}</Button> : v.public ? <span className="px-2 py-1 text-xs font-semibold rounded bg-[#D4AF37]/10 text-[#D4AF37] self-center">Live on Website</span> : null}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: "Candidates", val: workspace.placements.length, color: "#1E3A8A" },
          { label: "Interviews", val: workspace.interviews.length, color: "#06B6D4" },
          { label: "Fee Amount", val: `£${workspace.commission.total_fee.toLocaleString()}`, color: "#C5A059" },
          { label: "Opp. Finder", val: v.position_finder_name || "Unassigned", color: "#10B981" },
          { label: "Cand. Finder", val: v.candidate_finder_name || "Unassigned", color: "#8B5CF6" },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-lg border p-3">
            <p className="text-[10px] text-[#6B7280] uppercase font-medium">{s.label}</p>
            <p className="text-sm font-bold mt-0.5 truncate" style={{ color: s.color }}>{s.val}</p>
          </div>
        ))}
      </div>

      {/* Contract Status Banner */}
      {workspace && (() => {
        const cs = v.contract_status || "none";
        if (cs === "signed") return (
          <div className="flex items-center gap-2 px-4 py-2.5 bg-green-50 border border-green-200 rounded-lg">
            <ShieldCheck size={18} className="text-green-600" weight="bold" />
            <span className="text-sm text-green-700 font-medium">Contract Signed — This vacancy is active</span>
          </div>
        );
        if (cs === "admin_override") return (
          <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-lg">
            <ShieldCheck size={18} className="text-amber-600" weight="bold" />
            <span className="text-sm text-amber-700 font-medium">Admin Override — Working without signed contract</span>
          </div>
        );
        if (cs === "none" || cs === "draft" || cs === "sent") return (
          <div className="flex items-center justify-between px-4 py-2.5 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2">
              <WarningCircle size={18} className="text-red-500" weight="bold" />
              <span className="text-sm text-red-700 font-medium">
                {cs === "none" ? "No contract — Generate a contract to activate this vacancy" : cs === "draft" ? "Contract drafted — Send to client for signing" : "Contract sent — Awaiting client signature"}
              </span>
            </div>
            {cs === "none" && (
              <Button size="sm" onClick={() => { setContractForm({ fee_percentage: String(v.fee_percentage || 15), guarantee_days: "14", contact_person: v.contact_person || "", contact_email: v.contact_email || "", payment_terms: "Payment due within 14 days of invoice date.", special_terms: "" }); setShowCreateContract(true); }} className="bg-[#0F172A] gap-1" data-testid="generate-contract-btn"><FileText size={14} /> Generate Contract</Button>
            )}
          </div>
        );
        return null;
      })()}

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto bg-[#F3F4F6] p-1 rounded-lg">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors flex items-center gap-1 ${activeTab === t.key ? "bg-white text-[#0F172A] shadow-sm" : "text-[#6B7280]"}`}
            data-testid={`ws-tab-${t.key}`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg border border-[#E5E7EB] p-4 md:p-5 min-h-[300px]">
        {activeTab === "overview" && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm font-semibold text-[#0F172A] mb-2">Vacancy Details</h3>
                <div className="space-y-2 text-sm">
                  {[["Job Type", v.job_type], ["Hours", v.hours], ["Working Pattern", v.working_pattern], ["Experience", v.experience_required], ["Sector", v.sector], ["Start Date", v.start_date], ["Status", v.status], ["Contract", v.contract_status === "signed" ? "Signed" : v.contract_status === "admin_override" ? "Admin Override" : v.contract_status || "None"], ["Guarantee", (v.guarantee_days || 14) > 0 ? `${v.guarantee_days || 14} days` : "None"]].map(([k, val]) => val && (
                    <div key={k} className="flex justify-between py-1 border-b border-[#F3F4F6]"><span className="text-[#6B7280]">{k}</span><span className="font-medium">{val}</span></div>
                  ))}
                </div>
              </div>
              {workspace.company && (
                <div>
                  <h3 className="text-sm font-semibold text-[#0F172A] mb-2">Client Details</h3>
                  <div className="space-y-2 text-sm">
                    {[["Company", workspace.company.name], ["Email", workspace.company.email], ["Phone", workspace.company.phone], ["Website", workspace.company.website], ["Industry", workspace.company.industry]].map(([k, val]) => val && (
                      <div key={k} className="flex justify-between py-1 border-b border-[#F3F4F6]"><span className="text-[#6B7280]">{k}</span><span className="font-medium">{val}</span></div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {v.description && <div><h3 className="text-sm font-semibold mb-1">Description</h3><p className="text-sm text-[#6B7280]">{v.description}</p></div>}
            {v.contact_person && (
              <div><h3 className="text-sm font-semibold mb-1">Contact</h3>
                <p className="text-sm text-[#6B7280]">{v.contact_person} {v.contact_email ? `· ${v.contact_email}` : ""} {v.contact_phone ? `· ${v.contact_phone}` : ""}</p>
              </div>
            )}
          </div>
        )}

        {activeTab === "contract" && (
          <div className="space-y-4" data-testid="contract-tab">
            {workspace.contract ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold">Recruitment Services Agreement</h3>
                  <span className={`px-2 py-0.5 text-[10px] font-semibold rounded ${workspace.contract.status === "signed" ? "bg-green-100 text-green-700" : workspace.contract.status === "sent" ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-600"}`}>
                    {workspace.contract.status?.toUpperCase()}
                  </span>
                </div>
                <div className="bg-[#F9FAFB] rounded-lg p-4 space-y-2 text-sm">
                  {[
                    ["Contract No.", workspace.contract.contract_number],
                    ["Fee", `${workspace.contract.fee_percentage}%`],
                    ["Fee Amount", `\u00a3${workspace.contract.fee_amount?.toLocaleString()}`],
                    ["Total (inc. VAT)", `\u00a3${workspace.contract.total_amount?.toLocaleString()}`],
                    ["Guarantee", workspace.contract.guarantee_days > 0 ? `${workspace.contract.guarantee_days} days` : "None"],
                    ["Contact", workspace.contract.contact_person],
                    ["Email", workspace.contract.contact_email],
                    ["Created", workspace.contract.created_at ? new Date(workspace.contract.created_at).toLocaleString() : ""],
                    ["Sent", workspace.contract.sent_at ? new Date(workspace.contract.sent_at).toLocaleString() : "Not sent"],
                    ["Signed", workspace.contract.signed_at ? `${new Date(workspace.contract.signed_at).toLocaleString()} by ${workspace.contract.signed_by_name}` : "Not signed"],
                  ].filter(([, val]) => val).map(([k, val]) => (
                    <div key={k} className="flex justify-between py-1 border-b border-white">
                      <span className="text-[#6B7280]">{k}</span>
                      <span className="font-medium text-[#0F172A]">{val}</span>
                    </div>
                  ))}
                </div>
                {workspace.contract.special_terms && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-xs font-semibold text-amber-700 mb-1">Special Terms</p>
                    <p className="text-sm text-[#0F172A]">{workspace.contract.special_terms}</p>
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleDownloadContractPdf(workspace.contract.id)} className="gap-1" data-testid="ws-download-contract-pdf"><Download size={14} /> Download PDF</Button>
                  {["draft", "sent"].includes(workspace.contract.status) && (
                    <Button size="sm" onClick={() => handleSendContract(workspace.contract.id)} className="bg-[#1E3A8A] gap-1" data-testid="ws-send-contract"><PaperPlaneTilt size={14} /> {workspace.contract.status === "draft" ? "Send to Client" : "Resend"}</Button>
                  )}
                  {user?.role === "admin" && !["signed", "cancelled"].includes(workspace.contract.status) && (
                    <Button size="sm" variant="outline" onClick={() => handleContractOverride(workspace.contract.id)} className="gap-1 text-amber-600 border-amber-300" data-testid="ws-override-contract"><ShieldCheck size={14} /> Admin Override</Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <FileText size={48} className="text-[#D1D5DB] mx-auto mb-3" />
                <p className="text-sm text-[#6B7280] mb-3">No contract generated for this vacancy</p>
                <Button onClick={() => { setContractForm({ fee_percentage: String(v.fee_percentage || 15), guarantee_days: "14", contact_person: v.contact_person || "", contact_email: v.contact_email || "", payment_terms: "Payment due within 14 days of invoice date.", special_terms: "" }); setShowCreateContract(true); }} className="bg-[#0F172A] gap-1" data-testid="ws-generate-contract"><FileText size={14} /> Generate Contract</Button>
              </div>
            )}
          </div>
        )}

        {activeTab === "pipeline" && (
          <div className="space-y-3">
            {isContractRequired && (
              <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                <WarningCircle size={14} /> A signed contract is required before adding candidates. Go to the Contract tab to generate and send one.
              </div>
            )}
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">Pipeline ({workspace.placements.length})</h3>
              <Button variant="outline" size="sm" onClick={() => setShowAddCandidate(true)} disabled={isContractRequired} className="gap-1"><Plus size={12} /> Add</Button>
            </div>
            {workspace.placements.length === 0 ? <p className="text-sm text-[#6B7280] text-center py-8">No candidates in pipeline yet</p> : (
              <div className="space-y-2">
                {workspace.placements.map(p => {
                  const sm = STAGE_META[p.stage] || { label: p.stage, color: "#6B7280" };
                  return (
                    <div key={p.id} className="flex items-center justify-between py-2.5 px-3 bg-[#F9FAFB] rounded-lg" data-testid={`pipeline-item-${p.id}`}>
                      <div>
                        <p className="text-sm font-medium text-[#0F172A]">{p.candidate_name || "Unknown"}</p>
                        <p className="text-xs text-[#6B7280]">{p.candidate_email || ""}</p>
                        {p.guarantee_status === "active" && (
                          <p className="text-[10px] text-green-600 mt-0.5">Guarantee: {p.guarantee_days}d (expires {p.guarantee_expiry ? new Date(p.guarantee_expiry).toLocaleDateString() : ""})</p>
                        )}
                      </div>
                      <span className="px-2 py-0.5 text-[10px] font-semibold rounded text-white" style={{ backgroundColor: sm.color }}>{sm.label}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === "interviews" && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">Interviews ({workspace.interviews.length})</h3>
              <Button size="sm" onClick={() => setShowScheduleInterview(true)} className="bg-[#0F172A] gap-1"><Plus size={12} /> Schedule</Button>
            </div>
            {workspace.interviews.length === 0 ? <p className="text-sm text-[#6B7280] text-center py-8">No interviews scheduled</p> : (
              <div className="space-y-2">
                {workspace.interviews.map(int => (
                  <div key={int.id} className="bg-[#F9FAFB] rounded-lg p-3" data-testid={`interview-item-${int.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="text-sm font-medium text-[#0F172A]">{int.candidate_name || "Unknown"}</p>
                        <p className="text-xs text-[#6B7280]">{int.interview_type} interview · {int.scheduled_at ? new Date(int.scheduled_at).toLocaleString() : "TBD"}</p>
                      </div>
                      <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase rounded ${INT_STATUS_COLORS[int.status] || "bg-gray-100"}`}>{int.status}</span>
                    </div>
                    {int.meeting_link && <p className="text-xs text-[#1E3A8A] truncate">{int.meeting_platform ? `${int.meeting_platform}: ` : ""}{int.meeting_link}</p>}
                    {int.interviewer_name && <p className="text-xs text-[#6B7280]">Interviewer: {int.interviewer_name}</p>}
                    <div className="flex gap-2 mt-2">
                      {int.status === "scheduled" && <Button size="sm" variant="outline" onClick={() => handleInterviewAction(int.id, "confirm")} className="text-xs h-7">Confirm</Button>}
                      {(int.status === "scheduled" || int.status === "confirmed") && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => handleInterviewAction(int.id, "completed")} className="text-xs h-7">Complete</Button>
                          <Button size="sm" variant="outline" onClick={() => handleInterviewAction(int.id, "cancelled")} className="text-xs h-7 text-red-500">Cancel</Button>
                          <Button size="sm" onClick={() => handleInterviewAction(int.id, "complete_place")} className="text-xs h-7 bg-[#10B981]">Place</Button>
                        </>
                      )}
                      {(user?.role === "admin" || user?.role === "team_leader") && (
                        <Button size="sm" variant="outline" onClick={() => handleDeleteInterview(int.id)} className="text-xs h-7 border-red-200 text-red-500 hover:bg-red-50 ml-auto" data-testid={`delete-interview-${int.id}`}>
                          <Trash size={12} className="mr-1" /> Delete
                        </Button>
                      )}
                    </div>
                    {int.scorecard && int.scorecard.overall_rating > 0 && (
                      <div className="mt-2 p-2 bg-white rounded border text-xs">
                        <span className="font-medium">Score: {int.scorecard.overall_rating}/10</span>
                        <span className="ml-2 text-[#6B7280]">{int.scorecard.recommendation}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "notes" && (
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input placeholder="Add a note..." value={noteText} onChange={e => setNoteText(e.target.value)} onKeyDown={e => e.key === "Enter" && handleAddNote()} className="flex-1" data-testid="note-input" />
              <Button onClick={handleAddNote} size="sm" className="bg-[#0F172A]">Add</Button>
            </div>
            {(workspace.notes || []).length === 0 ? <p className="text-sm text-[#6B7280] text-center py-4">No notes yet</p> : (
              <div className="space-y-2">
                {workspace.notes.map(n => (
                  <div key={n.id} className="py-2 px-3 bg-[#F9FAFB] rounded-lg">
                    <p className="text-sm text-[#0F172A]">{n.content}</p>
                    <p className="text-[10px] text-[#6B7280] mt-1">{n.author_name} · {n.created_at ? new Date(n.created_at).toLocaleString() : ""}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "emails" && (
          <div className="space-y-2">
            {(workspace.emails || []).length === 0 ? <p className="text-sm text-[#6B7280] text-center py-8">No emails linked to this vacancy</p> : workspace.emails.map(e => (
              <div key={e.id} className="py-2 px-3 bg-[#F9FAFB] rounded-lg">
                <p className="text-sm font-medium text-[#0F172A]">{e.subject}</p>
                <p className="text-xs text-[#6B7280]">To: {e.to} · {e.created_at ? new Date(e.created_at).toLocaleString() : ""}</p>
              </div>
            ))}
          </div>
        )}

        {activeTab === "calls" && (
          <div className="space-y-2">
            {(workspace.calls || []).length === 0 ? <p className="text-sm text-[#6B7280] text-center py-8">No calls logged for this client</p> : workspace.calls.map(c => (
              <div key={c.id} className="py-2 px-3 bg-[#F9FAFB] rounded-lg">
                <p className="text-sm text-[#0F172A]">{c.call_status} · {c.duration}s</p>
                <p className="text-xs text-[#6B7280]">{c.notes} · {c.created_at ? new Date(c.created_at).toLocaleString() : ""}</p>
              </div>
            ))}
          </div>
        )}

        {activeTab === "commission" && (
          <div className="space-y-4">
            <h3 className="text-sm font-semibold">Commission Breakdown</h3>
            <div className="space-y-2">
              {[
                ["Placement Salary", `£${workspace.commission.placement_salary.toLocaleString()}`],
                ["Fee Rate", `${workspace.commission.fee_percentage}%`],
              ].map(([k, val]) => (
                <div key={k} className="flex justify-between py-2 border-b border-[#F3F4F6] text-sm"><span className="text-[#6B7280]">{k}</span><span className="font-medium">{val}</span></div>
              ))}
              <div className="flex justify-between py-2 bg-[#F0F4FF] px-3 rounded-lg text-sm"><span className="font-semibold text-[#1E3A8A]">Total Fee</span><span className="font-bold text-[#1E3A8A]">{"\u00a3"}{workspace.commission.total_fee.toLocaleString()}</span></div>
              <div className="flex justify-between py-2 text-sm"><span className="text-[#6B7280]">Opp. Finder (10%)</span><span className="text-[#10B981] font-medium">{"\u00a3"}{workspace.commission.position_finder_share.toLocaleString()}</span></div>
              <div className="flex justify-between py-2 text-sm"><span className="text-[#6B7280]">Cand. Finder (10%)</span><span className="text-[#10B981] font-medium">{"\u00a3"}{workspace.commission.candidate_finder_share.toLocaleString()}</span></div>
              <div className="flex justify-between py-3 bg-[#0F172A] text-white px-3 rounded-lg text-sm"><span className="font-semibold">Company Retained</span><span className="font-bold text-[#C5A059]">{"\u00a3"}{workspace.commission.company_retained.toLocaleString()}</span></div>
            </div>
            {workspace.invoice && (
              <div className="p-3 bg-[#F9FAFB] rounded-lg">
                <p className="text-xs text-[#6B7280] font-medium">Invoice Status</p>
                <p className="text-sm font-semibold text-[#0F172A]">{workspace.invoice.invoice_number} — {workspace.invoice.status}</p>
              </div>
            )}
          </div>
        )}
      </div>


      {/* Edit Vacancy Dialog */}
      {editingVacancy && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 mx-4 max-h-[80vh] overflow-y-auto" data-testid="edit-vacancy-dialog">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-base font-semibold text-[#0F172A]">Edit Vacancy</h3>
              <button onClick={() => setEditingVacancy(false)}><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <Input placeholder="Job Title *" value={editForm.title || ""} onChange={e => setEditForm({...editForm, title: e.target.value})} />
              <Input placeholder="Company Name" value={editForm.company_name || ""} onChange={e => setEditForm({...editForm, company_name: e.target.value})} />
              <Input placeholder="Location" value={editForm.location || ""} onChange={e => setEditForm({...editForm, location: e.target.value})} />
              <Input placeholder="Salary / hourly rate" value={editForm.salary || ""} onChange={e => setEditForm({...editForm, salary: e.target.value})} />
              <Input placeholder="Hours (e.g. 37.5 per week)" value={editForm.hours || ""} onChange={e => setEditForm({...editForm, hours: e.target.value})} />
              <Input placeholder="Working pattern (e.g. Mon-Fri days)" value={editForm.working_pattern || ""} onChange={e => setEditForm({...editForm, working_pattern: e.target.value})} />
              <Input placeholder="Fee %" value={editForm.fee_percentage || ""} onChange={e => setEditForm({...editForm, fee_percentage: e.target.value})} />
              <textarea className="w-full border rounded-lg p-2 text-sm" rows={3} placeholder="Description" value={editForm.description || ""} onChange={e => setEditForm({...editForm, description: e.target.value})} />
              <Input placeholder="Contact Person" value={editForm.contact_person || ""} onChange={e => setEditForm({...editForm, contact_person: e.target.value})} />
              <Input placeholder="Contact Email" value={editForm.contact_email || ""} onChange={e => setEditForm({...editForm, contact_email: e.target.value})} />
              <Input placeholder="Contact Phone" value={editForm.contact_phone || ""} onChange={e => setEditForm({...editForm, contact_phone: e.target.value})} />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setEditingVacancy(false)}>Cancel</Button>
              <Button className="bg-[#C5A059] text-white" onClick={async () => {
                try {
                  await api.put(`/vacancies/${workspace.vacancy.id}`, editForm);
                  toast.success("Vacancy updated");
                  setEditingVacancy(false);
                  const { data } = await api.get(`/vacancies/${workspace.vacancy.id}`);
                  setWorkspace(data);
                } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
              }} data-testid="save-vacancy-btn">Save Changes</Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Candidate Dialog */}
      {showAddCandidate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 mx-4 max-h-[70vh] overflow-y-auto" data-testid="add-candidate-dialog">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-[#0F172A]">Add Candidate to Pipeline</h2>
              <button onClick={() => setShowAddCandidate(false)}><X size={20} className="text-[#6B7280]" /></button>
            </div>
            <div className="space-y-2">
              {allCandidates.length === 0 ? <p className="text-sm text-[#6B7280]">No candidates available</p> :
                allCandidates.map(c => (
                  <button key={c.id} onClick={() => handleAddCandidate(c.id)} className="w-full flex items-center justify-between py-2 px-3 bg-[#F9FAFB] rounded-lg hover:bg-[#F0F4FF] transition-colors text-left" data-testid={`select-candidate-${c.id}`}>
                    <div><p className="text-sm font-medium">{c.first_name} {c.last_name}</p><p className="text-xs text-[#6B7280]">{c.current_role} {c.location ? `· ${c.location}` : ""}</p></div>
                    <Plus size={16} className="text-[#C5A059]" />
                  </button>
                ))
              }
            </div>
          </div>
        </div>
      )}

      {/* Schedule Interview Dialog */}
      {showScheduleInterview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 mx-4 max-h-[85vh] overflow-y-auto" data-testid="schedule-interview-dialog">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-[#0F172A]">Schedule Interview</h2>
              <button onClick={() => setShowScheduleInterview(false)}><X size={20} className="text-[#6B7280]" /></button>
            </div>
            <div className="space-y-3">
              <select className="w-full border rounded-lg px-3 py-2 text-sm" value={interviewForm.candidate_id} onChange={e => { const c = allCandidates.find(x => x.id === e.target.value); setInterviewForm({...interviewForm, candidate_id: e.target.value, candidate_name: c ? `${c.first_name} ${c.last_name}` : ""}); }} data-testid="interview-candidate-select">
                <option value="">Select Candidate *</option>
                {workspace.placements.map(p => <option key={p.candidate_id} value={p.candidate_id}>{p.candidate_name || p.candidate_id}</option>)}
              </select>
              <select className="w-full border rounded-lg px-3 py-2 text-sm" value={interviewForm.interview_type} onChange={e => setInterviewForm({...interviewForm, interview_type: e.target.value})}>
                <option value="phone_screen">Phone Screen</option>
                <option value="first">First Interview</option>
                <option value="second">Second Interview</option>
                <option value="technical">Technical</option>
                <option value="final">Final</option>
              </select>
              <Input type="datetime-local" value={interviewForm.scheduled_at} onChange={e => setInterviewForm({...interviewForm, scheduled_at: e.target.value})} data-testid="interview-datetime-input" />
              <Input type="number" placeholder="Duration (mins)" value={interviewForm.duration_minutes} onChange={e => setInterviewForm({...interviewForm, duration_minutes: e.target.value})} />
              <select className="w-full border rounded-lg px-3 py-2 text-sm" value={interviewForm.meeting_platform} onChange={e => setInterviewForm({...interviewForm, meeting_platform: e.target.value})}>
                <option value="">Meeting Platform</option>
                <option value="zoom">Zoom</option>
                <option value="teams">Microsoft Teams</option>
                <option value="meet">Google Meet</option>
                <option value="in_person">In Person</option>
              </select>
              <Input placeholder="Meeting Link" value={interviewForm.meeting_link} onChange={e => setInterviewForm({...interviewForm, meeting_link: e.target.value})} />
              <Input placeholder="Interviewer Name" value={interviewForm.interviewer_name} onChange={e => setInterviewForm({...interviewForm, interviewer_name: e.target.value})} />
              <textarea className="w-full border rounded-lg px-3 py-2 text-sm resize-none" rows={2} placeholder="Notes" value={interviewForm.notes} onChange={e => setInterviewForm({...interviewForm, notes: e.target.value})} />
              <Button onClick={handleScheduleInterview} className="w-full bg-[#0F172A]" data-testid="submit-interview-btn">Schedule Interview</Button>
            </div>
          </div>
        </div>
      )}

      {/* Create Contract Dialog */}
      {showCreateContract && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 mx-4 max-h-[85vh] overflow-y-auto" data-testid="create-contract-dialog">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-[#0F172A]">Generate Recruitment Agreement</h2>
              <button onClick={() => setShowCreateContract(false)}><X size={20} className="text-[#6B7280]" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-[#6B7280] font-medium mb-1 block">Recruitment Fee (%)</label>
                <select className="w-full border rounded-lg px-3 py-2 text-sm" value={contractForm.fee_percentage} onChange={e => setContractForm({...contractForm, fee_percentage: e.target.value})} data-testid="contract-fee-select">
                  <option value="15">15% (Standard)</option>
                  <option value="20">20%</option>
                  <option value="25">25%</option>
                  <option value="30">30%</option>
                  {user?.role === "admin" && <option value="custom">Custom</option>}
                </select>
                {contractForm.fee_percentage === "custom" && (
                  <Input type="number" placeholder="Custom fee %" className="mt-2" onChange={e => setContractForm({...contractForm, fee_percentage: e.target.value})} />
                )}
              </div>
              <div>
                <label className="text-xs text-[#6B7280] font-medium mb-1 block">Guarantee Period</label>
                <select className="w-full border rounded-lg px-3 py-2 text-sm" value={contractForm.guarantee_days} onChange={e => setContractForm({...contractForm, guarantee_days: e.target.value})} data-testid="contract-guarantee-select">
                  <option value="14">14 days (Standard)</option>
                  <option value="21">21 days</option>
                  <option value="30">30 days</option>
                  {user?.role === "admin" && <option value="0">No Guarantee</option>}
                </select>
              </div>
              <Input placeholder="Contact Person" value={contractForm.contact_person} onChange={e => setContractForm({...contractForm, contact_person: e.target.value})} data-testid="contract-contact-person" />
              <Input placeholder="Contact Email" value={contractForm.contact_email} onChange={e => setContractForm({...contractForm, contact_email: e.target.value})} data-testid="contract-contact-email" />
              <div>
                <label className="text-xs text-[#6B7280] font-medium mb-1 block">Payment Terms</label>
                <textarea className="w-full border rounded-lg px-3 py-2 text-sm resize-none" rows={2} value={contractForm.payment_terms} onChange={e => setContractForm({...contractForm, payment_terms: e.target.value})} />
              </div>
              <div>
                <label className="text-xs text-[#6B7280] font-medium mb-1 block">Special Terms (optional)</label>
                <textarea className="w-full border rounded-lg px-3 py-2 text-sm resize-none" rows={2} placeholder="Any additional terms..." value={contractForm.special_terms} onChange={e => setContractForm({...contractForm, special_terms: e.target.value})} />
              </div>
              <Button onClick={handleCreateContract} className="w-full bg-[#0F172A] hover:bg-[#1E293B]" data-testid="submit-contract-btn">Generate Contract</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
